/**
 * Player photos: an uploaded photo replaces the generated avatar. The photo's
 * public URL is stored in players.avatar_svg (and players.photo_url), so it shows
 * wherever the avatar already did. See supabase/migrations/20261001_player_photos.sql.
 */

export const PHOTO_BUCKET = 'player-photos';
/** Fired on window after the signed-in player's picture changes (header chip refreshes). */
export const PHOTO_CHANGED_EVENT = 'wnf-player-photo-changed';
const PUBLIC_MARKER = `/storage/v1/object/public/${PHOTO_BUCKET}/`;

/** The saved photo is cropped to this size (4:5, the card's picture area). */
export const PHOTO_WIDTH = 640;
export const PHOTO_HEIGHT = 800;
/** Zoom slider range: 1 = the photo just covers the frame. */
export const MAX_ZOOM = 3;

/** True for an uploaded photo; false for a generated avatar (a data: URI) or nothing. */
export function isUploadedPhoto(src: string | null | undefined): src is string {
  return Boolean(src && src.includes(PUBLIC_MARKER));
}

/** The image to show as a player's photo, or null when they use a generated avatar. */
export function photoOf(avatarSvg: string | null | undefined): string | null {
  return isUploadedPhoto(avatarSvg) ? avatarSvg : null;
}

/**
 * A kit portrait cut-out (the player on a transparent background, made by the bot as
 * "<ts>-kit-cutout.webp"): drawn straight onto the card, standing on its bottom edge,
 * instead of in a box. Ordinary photos and older portraits stay boxed.
 */
export function isCutout(src: string | null | undefined): boolean {
  return Boolean(src && isUploadedPhoto(src) && /-kit-cutout\.(webp|png)(\?|$)/i.test(src));
}

/**
 * The stadium version to show big (profile hero, full-size view): the portrait's
 * scene while the portrait is the live picture; an older portrait (which has its own
 * stadium) counts as its own scene.
 */
export function portraitScene(p: { photo_url?: string | null; photo_portrait_url?: string | null; photo_portrait_scene_url?: string | null }): string | null {
  if (!p.photo_portrait_url || p.photo_url !== p.photo_portrait_url) return null;
  if (p.photo_portrait_scene_url) return p.photo_portrait_scene_url;
  return isCutout(p.photo_portrait_url) ? null : p.photo_portrait_url;
}

/** "<player id>/<timestamp>.jpg": each upload gets a new name, so caches never show the old one. */
export function newPhotoPath(playerId: string, now = Date.now()): string {
  return `${playerId}/${now}.jpg`;
}

/** The storage path of an uploaded photo's public URL, or null. */
export function photoPathFromUrl(url: string | null | undefined): string | null {
  if (!isUploadedPhoto(url)) return null;
  const path = url.slice(url.indexOf(PUBLIC_MARKER) + PUBLIC_MARKER.length).split('?')[0];
  return path || null;
}

export interface Offset {
  x: number;
  y: number;
}

/** Scale at which an image of w×h just covers a frame of fw×fh. */
export function coverScale(w: number, h: number, fw: number, fh: number): number {
  return Math.max(fw / w, fh / h);
}

/**
 * Keeps the image covering the frame. The offset is how far the image's centre
 * sits from the frame's centre, in frame pixels.
 */
export function clampOffset(offset: Offset, w: number, h: number, fw: number, fh: number, zoom: number): Offset {
  const s = coverScale(w, h, fw, fh) * zoom;
  const maxX = Math.max(0, (w * s - fw) / 2);
  const maxY = Math.max(0, (h * s - fh) / 2);
  const clamp = (v: number, m: number) => Math.min(m, Math.max(-m, v)) || 0;
  return { x: clamp(offset.x, maxX), y: clamp(offset.y, maxY) };
}

/** The part of the image (in image pixels) that shows in the frame. */
export function sourceRect(w: number, h: number, fw: number, fh: number, zoom: number, offset: Offset) {
  const s = coverScale(w, h, fw, fh) * zoom;
  const { x, y } = clampOffset(offset, w, h, fw, fh, zoom);
  const sw = fw / s;
  const sh = fh / s;
  return { sx: w / 2 - (fw / 2 + x) / s, sy: h / 2 - (fh / 2 + y) / s, sw, sh };
}

/** A WNF kit portrait job (photo_portrait_jobs), as the photo sheet shows it. */
export interface PortraitJob {
  id: string;
  status: 'pending' | 'working' | 'done' | 'failed' | 'cancelled';
  error: string | null;
  created_at: string;
  finished_at: string | null;
}

/** "Making your kit portrait…" etc. for the photo sheet; null when there's nothing to say. */
export function portraitJobMessage(job: PortraitJob | null, now = Date.now()): { tone: 'busy' | 'bad'; text: string } | null {
  if (!job) return null;
  if (job.status === 'pending' || job.status === 'working') {
    return { tone: 'busy', text: job.error ? `Making your kit portrait — ${job.error.toLowerCase()}.` : 'Making your kit portrait… it takes about a minute and appears here when it’s done.' };
  }
  // Only mention a failure while it's recent (the last day).
  if (job.status === 'failed' && now - new Date(job.finished_at ?? job.created_at).getTime() < 86_400_000) {
    return { tone: 'bad', text: job.error ?? 'Couldn’t make your kit portrait this time.' };
  }
  return null;
}

/** What the kit-portrait progress says, changing every few seconds so it's clearly moving. */
const PORTRAIT_STEPS = [
  'Sizing you up for the kit…',
  'Pulling on the shirt…',
  'Stitching on the badge…',
  'Switching on the floodlights…',
  'Adding the finishing touches…',
];

export function portraitStep(elapsedMs: number): string {
  const i = Math.min(PORTRAIT_STEPS.length - 1, Math.floor(elapsedMs / 7000));
  return PORTRAIT_STEPS[i];
}

/**
 * A progress fraction for an unknown duration (usually 20–40 seconds): quick at
 * first, easing towards 95% so it never sits at 100% before the picture arrives.
 */
export function portraitProgress(elapsedMs: number): number {
  return 0.05 + 0.9 * (1 - Math.exp(-Math.max(0, elapsedMs) / 20000));
}
